import { request } from '@/api/http';
import type { DeleteResult, User, UserSourcesAssignment } from '@/api/types';

export type UserMembership = {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationSlug?: string | null;
  membershipRole: string;
  status?: string | null;
  joinedAt?: string | null;
  isInternal?: boolean;
};

export type UserMembershipList = {
  items: UserMembership[];
};

export type UserMembershipAssignment = {
  membershipId?: string;
  organizationId: string;
  membershipRole: string;
};

export type UserUpdatePayload = {
  email?: string;
  password?: string;
  name?: string;
  role?: string;
  plan?: string;
  channel?: string;
  telegram_chat_id?: string;
  source_ids?: string[];
  is_enabled?: boolean;
  organizationId?: string;
  membershipRole?: string;
  memberships?: UserMembershipAssignment[];
};

export type UserCreatePayload = UserUpdatePayload & {
  password?: string;
};

export const usersService = {
  list: () => request<User[]>('/users'),
  listPlatform: () => request<User[]>('/admin/platform-users'),
  createPlatform: (body: { email: string; password: string; name?: string; role: string; is_enabled?: boolean }) =>
    request<User>('/admin/platform-users', { method: 'POST', body: JSON.stringify(body) }),
  create: (body: UserCreatePayload) => request<User>('/users', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: UserUpdatePayload) =>
    request<User>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetPassword: (id: string, password: string) =>
    request<void>(`/admin/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  delete: (id: string, opts?: { remove_subscriptions?: boolean }) => {
    const q = opts?.remove_subscriptions === false ? '?remove_subscriptions=false' : '';
    return request<DeleteResult>(`/users/${id}${q}`, { method: 'DELETE' });
  },
  getSources: (userId: string) => request<UserSourcesAssignment>(`/users/${userId}/sources`),
  setSources: (userId: string, sourceIds: string[]) =>
    request<UserSourcesAssignment>(`/users/${userId}/sources`, {
      method: 'PUT',
      body: JSON.stringify({ source_ids: sourceIds }),
    }),
  getMemberships: (userId: string) => request<UserMembershipList>(`/users/${userId}/memberships`),
  addMembership: (userId: string, organizationId: string, membershipRole: string) =>
    request<UserMembership>(`/users/${userId}/memberships`, {
      method: 'POST',
      body: JSON.stringify({ organizationId, membershipRole }),
    }),
  updateMembership: (userId: string, membershipId: string, membershipRole: string) =>
    request<UserMembership>(`/users/${userId}/memberships/${membershipId}`, {
      method: 'PATCH',
      body: JSON.stringify({ membershipRole }),
    }),
  removeMembership: (userId: string, membershipId: string) =>
    request<{ ok: boolean }>(`/users/${userId}/memberships/${membershipId}`, { method: 'DELETE' }),
};
